import { Button, ChevronLeft, Plus, Tabs, useIsScrolled } from '../../modules/design-system';

export type MyPacksTabId = 'repackz' | 'drops' | 'deliveries';

export interface MyPacksTabDef {
  id: MyPacksTabId;
  label: string;
}

export interface MyPacksHeaderProps {
  tabs: MyPacksTabDef[];
  active: MyPacksTabId;
  onChange: (id: MyPacksTabId) => void;
  onHome: () => void;
  /** Fired by the right-slot "+ Add Delivery" CTA on the Deliveries tab.
   *  Currently a placeholder — wires to the host's delivery creation flow
   *  once that endpoint lands. */
  onAddDelivery?: () => void;
}

/**
 * My Packs header — left "Home" pill, middle tab group with custom styling
 * for the dark page (white-on-transparent idle, dark-on-white active),
 * right-slot "+ Add Delivery" CTA shown only on the Deliveries tab.
 * Layout flexes:
 *   < 600px → home + tab group stack vertically.
 *   ≥ 600px → all controls in a single horizontal row.
 */
export function MyPacksHeader({
  tabs,
  active,
  onChange,
  onHome,
  onAddDelivery,
}: MyPacksHeaderProps) {
  const scrolled = useIsScrolled(8);
  return (
    <header
      data-co-component="MyPacksHeader"
      data-scrolled={scrolled || undefined}
      className={
        'sticky top-0 z-30 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ')
      }
      style={{
        fontFamily: 'var(--co-MyPacksHeader-fontFamily), Inter, system-ui, sans-serif',
      }}
    >
      <div
        className="
          mx-auto flex w-full max-w-350 flex-wrap items-center gap-3
          px-4 py-4 @[600px]:px-6 @[600px]:py-5
        "
      >
        <div className="flex flex-1 items-center justify-start">
          <HomeButton onClick={onHome} />
        </div>

        {/* Mobile centre-title (Figma 278:7388). Sits in its own flex
         *  slot between the back-icon and an empty right spacer so the
         *  title is geometrically centred relative to the back/spacer
         *  pair (independent of the back-icon's intrinsic width). */}
        <h1
          className="
            flex-1 text-center text-[18px] font-bold tracking-[-0.3px]
            @[600px]:hidden
          "
          style={{ color: 'var(--co-Tabs-tabIdleText, #FFFFFF)' }}
        >
          My Packs
        </h1>

        <div className="order-3 w-full @[600px]:order-2 @[600px]:w-auto @[600px]:flex-1">
          <Tabs<MyPacksTabId> tabs={tabs} active={active} onChange={onChange} />
        </div>

        <div
          className="
            order-2 flex flex-1 items-center justify-end
            @[600px]:order-3
          "
        >
          {/* Right-slot control only on ≥600px and only on the Deliveries
           *  tab. On mobile (and on the other tabs on desktop) the slot
           *  stays empty (flex-1 spacer) so the title above stays
           *  centred. */}
          <div className="hidden @[600px]:flex">
            {active === 'deliveries' && (
              <AddDeliveryButton onClick={onAddDelivery ?? (() => {})} />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function HomeButton({ onClick }: { onClick: () => void }) {
  return (
    <>
      {/* Mobile: round icon-only button (Figma 278:7388). h-9/w-9 to
       *  match the tab pill height (36px on mobile). */}
      <div className="@[600px]:hidden">
        <Button
          variant="glass-frosted"
          size="md"
          onClick={onClick}
          aria-label="Home"
          className="!h-9 !w-9"
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </Button>
      </div>

      {/* ≥600px: full pill with icon + "Home" label. */}
      <div className="hidden @[600px]:block">
        <Button
          variant="glass-frosted"
          size="lg"
          onClick={onClick}
          aria-label="Home"
          leftIcon={<ChevronLeft size={24} strokeWidth={2} />}
        >
          Home
        </Button>
      </div>
    </>
  );
}

function AddDeliveryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="glass-frosted"
      size="lg"
      onClick={onClick}
      rightIcon={<Plus size={22} strokeWidth={2.5} />}
    >
      Add Delivery
    </Button>
  );
}

